import React from 'react'
import { Redirect } from 'react-router'

import { PrivateWorkspaceRouteExtraProps } from '../../workspace/containers'
import { NotFoundView, RouteProps, routes } from '@sketch/modules-common'
import { WorkspaceProfileLayout } from '../containers/WorkspaceProfileLayout'
import { useIsProfileCreated } from '../hooks'
import { useGetWorkspaceProfile } from '../hooks/useGetWorkspaceProfile'
import { WorkspaceSidebarLayoutExtraProps } from '../../workspace/components/WorkspaceSidebarLayout/WorkspaceSidebarLayout'
import { ProfileInfo } from '../components/ProfileInfo'
import { ProfileTopBanners } from '../components/ProfileTopBanners'
import { LoadingState } from '@sketch/components'

interface WorkspaceProfileEditViewRouteProps
  extends RouteProps<'WORKSPACE_PROFILE_EDIT'>,
    PrivateWorkspaceRouteExtraProps {}

type WorkspaceProfileEditViewProps = WorkspaceProfileEditViewRouteProps &
  WorkspaceSidebarLayoutExtraProps

const WorkspaceProfileEditView = (props: WorkspaceProfileEditViewProps) => {
  const { workspace, NavbarPortal, useOverrideLayoutProps } = props

  useOverrideLayoutProps({
    title: 'Profile',
  })

  const { profile, loading, error } = useGetWorkspaceProfile({
    variables: {
      workspaceIdentifier: workspace.identifier,
    },
  })

  if (loading) {
    return <LoadingState />
  }

  if (error || !profile) {
    return <NotFoundView />
  }

  const profileData = {
    identifier: profile?.identifier || '',
    description: profile?.description || '',
    websiteUrl: profile?.websiteUrl || '',
    shortUrlName: profile?.shortUrlName || '',
  }

  return (
    <>
      <NavbarPortal>
        <ProfileTopBanners
          workspace={workspace}
          profileData={{
            identifier: profileData.identifier,
            shortUrlName: profileData.shortUrlName,
          }}
          isPublic={profile?.isPublic === true}
          isLoading={loading}
        />
      </NavbarPortal>

      <WorkspaceProfileLayout
        workspaceId={workspace.identifier}
        isProfileEditRoute={true}
        profile={profile}
        profileInfo={<ProfileInfo profile={profile || {}} />}
      />
    </>
  )
}

// At the moment only Admins of the workspace can access
// Profile Edit page. And this is the first component after
// PrivateWorkspaceRoute where we can check this.
//
// If the profile is not created yet we also wanna prevent this page
// to be seen.
const WorkspaceProfileEditViewGuard = (
  props: WorkspaceProfileEditViewProps
) => {
  const { workspace } = props
  const workspaceId = workspace.identifier

  const { isProfileCreated, loading } = useIsProfileCreated(workspaceId)

  if (workspace.userRole !== 'ADMIN' || (!loading && !isProfileCreated)) {
    return <Redirect to={routes.WORKSPACE_SHARES.create({ workspaceId })} />
  }

  if (loading) {
    return <LoadingState />
  }

  return <WorkspaceProfileEditView {...props} />
}

export default WorkspaceProfileEditViewGuard
